import { useMemo, useState, useEffect, useRef } from "react";
import Icon from "../../Elements/Icon/Icon.tsx";
import UserNavigatorMenu from "../../Reusable/UserNavigatorMenu/UserNavigatorMenu";
import useScrollTrigger from "../../../hooks/useScrollTrigger.js";
import useMediaQueries from "../../../hooks/useMediaQueries.js";
import useMailSender from "../../../hooks/useMailSender.js";
import apiRequest from "../../../utils/ApiRequest/apiRequest.js";
import Image from "components/Elements/Image/Image.tsx";
import LanguagePicker from "./LanguagePicker.js";
import useApp from "hooks/useApp.ts";
import MegaMenu from "./components/MegaMenu/index.tsx";
import useOrganization from "hooks/useOrganization.ts";
import SubscriptionExpirationReminder from "../Page/components/SubscriptionExpirationReminder/index.tsx";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import { UserBrandedHeader } from "../../Structure/Header/UserBrandedHeader.js";
import { GeneralLinks } from "./GeneralLinks";
import { HeaderDrawer } from "./HeaderDrawer";
import { LinkToNewTab } from "../../Elements/Link/Link.js";
import { useUser } from "../../../contexts/userContext/userContext.js";
import { useModal } from "contexts/modalContext/modalContext.js";

import "./Header.scss";

const DELAY_MS = 350;

//showBlackFridayHeader: optional prop, if true - show black friday header.
const Header = ({ showBlackFridayHeader, isCustomHeader }) => {
  const { getMailSenderBrandedHeader, isMailSenderSubscribed } =
    useMailSender();
  const [showMegaMenuSection, setShowMegaMenuSection] = useState("");
  const { closeModal } = useModal();
  const { theme } = useApp();
  const filledHeaderClassName = `show-white-header header-${theme}-theme`;

  const { isLoggedIn } = useUser();

  const {
    getCurrentPageType,
    getCurrentPage,
    getBlackFridayTime,
    getBlackFridayTimeCookie,
  } = useContent();
  const { logo, fetchLogo, checkCurrDomainIsOrgDomain } = useOrganization();

  useEffect(() => {
    fetchLogo();
  }, []);

  const [isClicked, setIsClicked] = useState(false);
  const [countNewNotifications, setCountNewNotifications] = useState(null);
  const { desktopCondition, tabletCondition, mobileCondition } =
    useMediaQueries();

  const controlPosition = useMemo(() => {
    const elem = document.querySelector(".content-holder") ||
      document.querySelector(".home-content") || {
        offsetTop: window.innerHeight - 200,
      };
    if (elem)
      return (
        elem.offsetTop - (document.querySelector(".home-content") ? 200 : 0)
      );
    return null;
  }, [window.innerWidth, window.innerHeight]);

  const { moreThanNumber: scrolledTop, scrolledUp } = useScrollTrigger(
    controlPosition ? controlPosition - 300 : 700
  );

  const { moreThanNumber: scrolledBot } = useScrollTrigger(
    controlPosition ? controlPosition + 50 : 850
  );

  const galleryPage = useMemo(() => {
    return (
      getCurrentPageType() &&
      (getCurrentPageType().indexOf("gallery") >= 0 ||
        getCurrentPageType().indexOf("collection") >= 0 ||
        getCurrentPage() === "gallery" ||
        getCurrentPage() === "collection" ||
        getCurrentPage() === "home" ||
        getCurrentPage() === "advertising")
    );
  }, [getCurrentPageType(), getCurrentPage()]); //force white header

  const [navClass, setNavClass] = useState(null);

  useEffect(() => {
    setNavClass(
      galleryPage ? "show-transparent-header" : filledHeaderClassName
    );
  }, [galleryPage, filledHeaderClassName]);

  useEffect(() => {
    const navElement = document.querySelector("nav");

    if (navElement) {
      if (showMegaMenuSection) {
        navElement.classList.add("show-mega-menu-header");
      } else {
        navElement.classList.remove("show-mega-menu-header");
      }
    }
  }, [showMegaMenuSection]);

  //show white header on pages without backgroundImage.
  //for backgroundImage pages, show header without white background.
  //if scrolled to page content, hide header.
  //if the header is not displayed but user scrolled up > show the header.
  useMemo(() => {
    const homePageShowWhiteHeader =
      getCurrentPageType() && getCurrentPage() === "home" && scrolledBot;
    if (
      !galleryPage ||
      (scrolledBot && scrolledUp && window.scrollY !== 0) ||
      homePageShowWhiteHeader
    ) {
      setNavClass(
        getBlackFridayTime() && getBlackFridayTimeCookie()
          ? `${filledHeaderClassName} black-friday`
          : filledHeaderClassName
      );
    } else {
      !scrolledTop
        ? setNavClass(
            getBlackFridayTime() && getBlackFridayTimeCookie()
              ? "show-transparent-header black-friday"
              : "show-transparent-header"
          )
        : setNavClass(null);
    }
  }, [
    scrolledTop,
    scrolledBot,
    scrolledUp,
    galleryPage,
    getCurrentPage(),
    getCurrentPageType(),
    getBlackFridayTime(),
    showBlackFridayHeader,
    filledHeaderClassName,
  ]);

  useEffect(() => {
    //if page is expended and side menu is open - hide it.
    if (desktopCondition) {
      setIsClicked(false);
    }
    return () => {
      setIsClicked(false);
      document.body.classList.remove("fixed-position");
    };
  }, [desktopCondition]);

  const countNotifications = async () => {
    const response = await apiRequest("Notifications", "CountNewNotifications");
    if (response.status === "success") {
      if (response.data) {
        setCountNewNotifications(response.data);
      } else {
        setCountNewNotifications(0);
      }
    }
  };

  useEffect(() => {
    if (isLoggedIn()) {
      countNotifications();
    }
  }, []);

  const isShowJumbomailHeader =
    !galleryPage ||
    !(galleryPage && isMailSenderSubscribed && getMailSenderBrandedHeader);

  const isNotWhiteHeader = !navClass?.includes(filledHeaderClassName);

  // remove all but icon and language from invite or subuser link page
  if (
    getCurrentPage() === "invite" ||
    getCurrentPage() === "sub-user-complete"
  ) {
    if (checkCurrDomainIsOrgDomain()) {
      return null;
    } else {
      return <InvitePageCustomHeader navClass={navClass} />;
    }
  }

  return (
    <nav className={navClass}>
      <SubscriptionExpirationReminder
        showMegaMenuSection={showMegaMenuSection}
      />
      <div className="header">
        {(!isShowJumbomailHeader && tabletCondition) || desktopCondition ? (
          <DesktopHeader
            countNewNotifications={countNewNotifications}
            setCountNewNotifications={setCountNewNotifications}
            isShowJumbomailHeader={isShowJumbomailHeader}
            isWhite={isNotWhiteHeader}
            showMegaMenuSection={showMegaMenuSection}
            setShowMegaMenuSection={setShowMegaMenuSection}
            isCustomHeader={isCustomHeader}
          />
        ) : (
          <div
            className={`mobile-header ${
              checkCurrDomainIsOrgDomain() ? "custom-mobile-header" : ""
            }`}
            onClick={() => {
              window.scrollTo(0, 0);
            }}
          >
            <Icon
              name="hamburger"
              iconClass="header-hamburger-button"
              onClick={(e) => {
                e.stopPropagation();
                closeModal();
                setIsClicked((prev) => !prev);
                document.body.classList.add("fixed-position");
              }}
            />
            <div className="mobile-header-logo">
              {isShowJumbomailHeader ? (
                <LinkToNewTab page="home">
                  {logo ? (
                    <Image src={logo} className="custom-logo" />
                  ) : checkCurrDomainIsOrgDomain() ? null : (
                    <Icon
                      name="jumbomail-icon"
                      title="JUMBOmail"
                      alt="JUMBOmail"
                      ext="png"
                    />
                  )}
                </LinkToNewTab>
              ) : (
                <UserBrandedHeader
                  isMobile={true}
                  hideLinks={true}
                  isWhite={isNotWhiteHeader}
                />
              )}
              <HeaderDrawer
                isClicked={isClicked}
                isShowJumbomailHeader={isShowJumbomailHeader}
                set={setIsClicked}
              />
            </div>
            <UserNavigatorMenu
              countNewNotifications={countNewNotifications}
              setCountNewNotifications={setCountNewNotifications}
              isWhite={isNotWhiteHeader || !showMegaMenuSection}
            />
          </div>
        )}
      </div>
    </nav>
  );
};

const InvitePageCustomHeader = ({ navClass }) => {
  const { logo, fetchLogo, checkCurrDomainIsOrgDomain } = useOrganization();

  useEffect(() => {
    fetchLogo();
  }, []);

  return (
    <nav className={navClass}>
      <div className="header">
        <div className="links links-row">
          <LinkToNewTab exact page={"home"} className="no-hover">
            {logo && checkCurrDomainIsOrgDomain() ? (
              <Image src={logo} className="custom-logo" />
            ) : null}
            {checkCurrDomainIsOrgDomain() ? null : (
              <Image
                name="jumbomail-logo-black"
                title="JUMBOmail"
                alt="JUMBOmail"
              />
            )}
          </LinkToNewTab>
        </div>
        <div className="user-navigator-menu">
          <div className="login-signup-buttons">
            <LanguagePicker />
          </div>
        </div>
      </div>
    </nav>
  );
};

const DesktopHeader = ({
  isShowJumbomailHeader,
  isWhite,
  setCountNewNotifications,
  countNewNotifications,
  showMegaMenuSection,
  setShowMegaMenuSection,
  isCustomHeader,
}) => {
  const { theme } = useApp();
  // ---------------------------------------------------------------------------------
  // Timer allows to keep the mega menu open when hovering over the different sections
  // ---------------------------------------------------------------------------------

  const timerRef = useRef(null);

  const openDropdown = (section) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setShowMegaMenuSection(section);
  };

  const closeDropdown = () => {
    timerRef.current = setTimeout(() => {
      setShowMegaMenuSection("");
    }, DELAY_MS);
  };

  const onMouseEnter = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setShowMegaMenuSection(showMegaMenuSection);
  };

  // ---------------------------------------------------------------------------------
  // ---------------------------------------------------------------------------------

  return (
    <>
      {isShowJumbomailHeader ? (
        <GeneralLinks
          direction="row"
          whiteLogo={
            theme === "dark" || (showMegaMenuSection ? false : isWhite)
          }
          openDropdown={openDropdown}
          closeDropdown={closeDropdown}
          shownSection={showMegaMenuSection}
          isCustomHeader={isCustomHeader}
        />
      ) : (
        <UserBrandedHeader
          isMobile={false}
          isWhite={theme === "dark" || isWhite}
        />
      )}
      <div className="flex-spacer" />
      <UserNavigatorMenu
        countNewNotifications={countNewNotifications}
        setCountNewNotifications={setCountNewNotifications}
        isWhite={theme === "dark" || isWhite}
        setShowMegaMenuSection={setShowMegaMenuSection}
      />
      <MegaMenu
        shownSection={showMegaMenuSection}
        setShowMegaMenuSection={setShowMegaMenuSection}
        onMouseEnter={onMouseEnter}
        onMouseLeave={() => setShowMegaMenuSection("")}
      />
    </>
  );
};
export default Header;
